(() => {
  if (window.__AM_PRODUCT_CARD_SCRIPT_INITIALIZED__) return;
  window.__AM_PRODUCT_CARD_SCRIPT_INITIALIZED__ = true;

  let swiperObserver;
  let swiperLoadPromise;

  const stopObservingSwiper = (container) => {
    swiperObserver?.unobserve(container);
  };

  const ensureSwiperReady = () => {
    if (typeof Swiper !== 'undefined') return Promise.resolve();
    if (swiperLoadPromise) return swiperLoadPromise;
    if (typeof window.__amLoadSwiper !== 'function') return Promise.resolve();

    swiperLoadPromise = window.__amLoadSwiper().catch(() => {});
    return swiperLoadPromise;
  };

  const isMobileGridGallery = (container) => {
    if (!window.matchMedia?.('(pointer: coarse)').matches) return false;
    return Boolean(
      container.closest(
        'results-list, .product-grid, product-recommendations, .am-collection-tabs, .am-collection-tiles, .am-scent-finder'
      )
    );
  };

  const resetGalleryInlineStyles = (container) => {
    if (container.swiper && typeof container.swiper.destroy === 'function') {
      container.swiper.destroy(true, true);
    }
    container.querySelector('.swiper-wrapper')?.removeAttribute('style');
    container.querySelectorAll('.swiper-slide').forEach((slide) => slide.removeAttribute('style'));
  };

  const attachAxisLock = (container) => {
    if (container.dataset.amAxisLockInit === 'true') return;
    container.dataset.amAxisLockInit = 'true';

    let startX = 0;
    let startY = 0;
    let axis = null;

    container.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.touches[0];
        if (!touch) return;
        startX = touch.clientX;
        startY = touch.clientY;
        axis = null;
      },
      { passive: true }
    );

    container.addEventListener(
      'touchmove',
      (event) => {
        const touch = event.touches[0];
        if (!touch) return;

        if (!axis) {
          const deltaX = Math.abs(touch.clientX - startX);
          const deltaY = Math.abs(touch.clientY - startY);
          if (deltaX < 6 && deltaY < 6) return;
          axis = deltaX > deltaY * 1.2 ? 'x' : 'y';
          container.style.overflowX = axis === 'y' ? 'hidden' : 'auto';
        }
      },
      { passive: true }
    );

    container.addEventListener(
      'touchend',
      () => {
        container.style.overflowX = '';
      },
      { passive: true }
    );
  };

  const initMobileGridGallery = (container) => {
    if (!container || container.dataset.swiperInit === '1') return false;
    if (container.classList.contains('custom-mood-swiper')) return false;
    if (!isMobileGridGallery(container)) return false;

    resetGalleryInlineStyles(container);
    attachAxisLock(container);
    container.dataset.swiperInit = '1';
    stopObservingSwiper(container);
    return true;
  };

  const initCardSwiper = (container) => {
    if (!container || container.dataset.swiperInit === '1') return;

    if (initMobileGridGallery(container)) return;

    if (typeof Swiper === 'undefined') return;

    const nextEl = container.querySelector('.swiper-button-next');
    const prevEl = container.querySelector('.swiper-button-prev');
    const slides = container.querySelectorAll('.swiper-wrapper .swiper-slide');
    if (!slides.length) return;

    const isMoodSwiper = container.classList.contains('custom-mood-swiper');
    const paginationEl = isMoodSwiper
      ? container.closest('.am-collection-tiles__mood-card')?.querySelector('.am-collection-tiles__mood-pagination')
      : null;

    if (slides.length <= 1) {
      if (nextEl) nextEl.style.display = 'none';
      if (prevEl) prevEl.style.display = 'none';
      if (paginationEl) paginationEl.style.display = 'none';
    }

    [nextEl, prevEl].forEach((el) => {
      if (!el || el.dataset.amSwiperArrowInit === 'true') return;
      el.dataset.amSwiperArrowInit = 'true';
      el.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
        },
        { passive: false }
      );
    });

    const config = {
      loop: slides.length > 2,
      rewind: slides.length > 1,
      spaceBetween: isMoodSwiper ? 0 : 10,
      slidesPerView: 1,
      slidesPerGroup: 1,
      nested: true,
      observer: false,
      observeParents: false,
    };

    if (slides.length > 1) {
      config.navigation = { nextEl, prevEl };
      if (paginationEl) {
        config.pagination = { el: paginationEl, clickable: true };
      }
    }

    new Swiper(container, config);
    container.dataset.swiperInit = '1';
    stopObservingSwiper(container);
  };

  const initPendingMobileGridGalleries = (root = document) => {
    if (!root?.querySelectorAll) return;
    root.querySelectorAll('.custom-pro-image.swiper:not([data-swiper-init="1"])').forEach((container) => {
      initMobileGridGallery(container);
    });
  };

  const initSwipersWhenVisible = (root = document) => {
    if (!root?.querySelectorAll) return;

    initPendingMobileGridGalleries(root);

    const containers = root.querySelectorAll('.custom-pro-image.swiper:not([data-swiper-init="1"])');
    if (!containers.length) return;

    if (!swiperObserver) {
      swiperObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) initCardSwiper(entry.target);
          });
        },
        { rootMargin: '80px', threshold: 0.01 }
      );
    }

    containers.forEach((container) => swiperObserver.observe(container));
  };

  const scheduleCardSwipersIn = (root = document) => {
    const schedule = () => {
      if (root !== document && !root.isConnected) return;
      initSwipersWhenVisible(root);
    };

    initPendingMobileGridGalleries(root);

    if (typeof Swiper === 'undefined') {
      ensureSwiperReady().then(schedule);
    } else {
      schedule();
    }
  };

  const destroyCardSwipersIn = (root) => {
    if (!root?.querySelectorAll) return;

    root.querySelectorAll('.custom-pro-image.swiper').forEach((container) => {
      stopObservingSwiper(container);
      if (container.swiper && typeof container.swiper.destroy === 'function') {
        container.swiper.destroy(true, true);
      }
      delete container.dataset.swiperInit;
      delete container.dataset.amAxisLockInit;
    });
  };

  const remountCardSwipersIn = (root) => {
    destroyCardSwipersIn(root);
    scheduleCardSwipersIn(root);
  };

  window.__amDestroyCardSwipersIn = destroyCardSwipersIn;
  window.__amInitCardSwipersIn = scheduleCardSwipersIn;
  window.__amRemountCardSwipersIn = remountCardSwipersIn;

  const bootSwipers = () => {
    initPendingMobileGridGalleries();

    const schedule = () => {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => scheduleCardSwipersIn(), { timeout: 2000 });
      } else {
        setTimeout(() => scheduleCardSwipersIn(), 500);
      }
    };

    ensureSwiperReady().then(schedule);
  };

  const handleGridUpdate = async (event) => {
    const renderPromise = event?.promise instanceof Promise ? event.promise : Promise.resolve();
    const target = event.target instanceof Element ? event.target : null;
    const section = target?.closest('.shopify-section');

    try {
      await renderPromise;
    } catch {}

    requestAnimationFrame(() => {
      const root =
        section?.querySelector('.product-grid, results-list') ||
        target?.querySelector?.('.product-grid, results-list') ||
        document.querySelector('results-list .product-grid, .product-grid, results-list');

      if (root) remountCardSwipersIn(root);
    });
  };

  const onReady = () => {
    bootSwipers();

    document.addEventListener(
      'click',
      (event) => {
        const ctaInInfo = event.target.closest('.add-to__cart__mobile--in-info');
        const isButton = event.target.closest('button');
        const isBackInStockControl = event.target.closest(
          '[data-back-in-stock-trigger], [data-back-in-stock-close], [data-back-in-stock-submit], [data-back-in-stock-resend]'
        );

        if (ctaInInfo && !isButton) {
          event.preventDefault();
          event.stopPropagation();
        } else if (ctaInInfo && isButton && !isBackInStockControl) {
          event.stopPropagation();
        }
      },
      true
    );

    ['shopify:collection:update', 'shopify:search:update', 'shopify:section:load'].forEach((eventName) => {
      document.addEventListener(eventName, handleGridUpdate);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }

  if (!window.__AM_PRODUCT_CARD_BUTTON_GUARD__) {
    window.__AM_PRODUCT_CARD_BUTTON_GUARD__ = true;

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const buttonArea = event.target.closest('.product-card__buttons');
      if (!buttonArea) return;

      const isInsideForm = event.target.closest('form');
      const isWishlistButton = event.target.closest(
        '.custom-btn-wishlist, .custom-btn-wishlist-mobile, .wishlist-engine, .wishlist-engine-button'
      );
      const isBackInStockTrigger = event.target.closest(
        '[data-back-in-stock-trigger], [data-back-in-stock-close], [data-back-in-stock-submit], [data-back-in-stock-resend], [data-back-in-stock-email]'
      );

      if (isWishlistButton || isBackInStockTrigger || !isInsideForm) {
        event.preventDefault();
        event.stopPropagation();
      }
    });
  }
})();
